import type { Json } from '../supabase/database.types';
import type { SupabaseClient } from '../supabase/client';
import type {
  CheckoutFulfillmentQuoteInput,
  FulfillmentOutcome,
  FulfillmentQuote,
  PreparedFulfillmentQuoteInput,
  PublicFulfillmentOptions,
  SaveStoreFulfillmentInput,
  StoreFulfillmentSettings,
} from '../types/store-fulfillment';

function requirePayload<T>(data: unknown): T {
  if (!data || typeof data !== 'object') throw new Error('Empty fulfillment response');
  return data as T;
}

export async function getStoreFulfillmentSettings(
  client: SupabaseClient,
  storeId: string,
): Promise<StoreFulfillmentSettings> {
  const { data, error } = await client.rpc('get_store_fulfillment_settings_v1', {
    target_store_id: storeId,
  });
  if (error) throw error;
  return requirePayload<StoreFulfillmentSettings>(data);
}

export async function saveStoreFulfillmentSettings(
  client: SupabaseClient,
  input: SaveStoreFulfillmentInput,
): Promise<FulfillmentOutcome<StoreFulfillmentSettings>> {
  const { data, error } = await client.rpc('save_store_fulfillment_settings_v1', {
    target_store_id: input.storeId,
    expected_revision: input.expectedRevision,
    config: {
      currency: input.currency,
      deliveryFeeCents: input.deliveryFeeCents,
      pickupPlaces: input.pickupPlaces.map((place) => ({ ...place })),
    },
  });
  if (error) throw error;
  return requirePayload<FulfillmentOutcome<StoreFulfillmentSettings>>(data);
}

export async function getPublicFulfillmentOptions(
  client: SupabaseClient,
  storeId: string,
): Promise<PublicFulfillmentOptions | null> {
  const { data, error } = await client.rpc('get_public_store_fulfillment_options_v1', {
    target_store_id: storeId,
  });
  if (error) throw error;
  return data === null ? null : requirePayload<PublicFulfillmentOptions>(data);
}

export async function quoteCheckoutFulfillment(
  client: SupabaseClient,
  input: CheckoutFulfillmentQuoteInput,
  signal?: AbortSignal,
): Promise<FulfillmentOutcome<FulfillmentQuote>> {
  const request = client.rpc('quote_checkout_fulfillment_v1', {
    target_store_id: input.storeId,
    selection: { ...input.selection },
  });
  const { data, error } = await (signal ? request.abortSignal(signal) : request);
  if (error) throw error;
  return requirePayload<FulfillmentOutcome<FulfillmentQuote>>(data);
}

export async function quotePreparedFulfillment(
  client: SupabaseClient,
  input: PreparedFulfillmentQuoteInput,
  signal?: AbortSignal,
): Promise<FulfillmentOutcome<FulfillmentQuote>> {
  const request = client.rpc('quote_admin_prepared_fulfillment_v1', {
    target_store_id: input.storeId,
    selection: { ...input.selection },
    lines: input.lines.map((line) => ({ ...line })) as Json,
  });
  const { data, error } = await (signal ? request.abortSignal(signal) : request);
  if (error) throw error;
  return requirePayload<FulfillmentOutcome<FulfillmentQuote>>(data);
}
